import { useState, useEffect, useRef } from "react";
import { Box, Stack, FormControl, Select, MenuItem, CircularProgress, Alert, useTheme, Typography } from "@mui/material";
import { delftBlue } from "@/theme/colors";
import KPICard from "./components/KPICard";
import PeriodSelector, { PeriodDropdown } from "./components/PeriodSelector";
import StatusIndicator from "./components/StatusIndicator";
import OverviewChart from "./components/OverviewChart";
import QuickActions from "./components/QuickActions";
import CollapsibleSection from "./components/CollapsibleSection";
import HandshakeIcon from "@/assets/material-symbols_handshake-outline.svg";
import ActivityIcon from "@/assets/Icon-1.svg";
import FormIcon from "@/assets/lets-icons_form.svg";
import DownArrowIcon from "@/assets/mingcute_down-line.svg";
import { useGetDashboardStatsQuery, useGetDashboardOverviewQuery, useGetCompaniesQuery } from "@/app/features/dashboard/dashboardApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";

const DashboardPage = () => {
  const theme = useTheme();
  const [period, setPeriod] = useState("this-month");
  const [companyId, setCompanyId] = useState("");
  const user = useSelector(selectUser);

  const isSuperAdmin = user?.role === Roles.SUPER_ADMIN;

  // Fetch companies for Super Admin filter
  const { data: companiesData, isLoading: isLoadingCompanies } = useGetCompaniesQuery(
    { page: 1, limit: 1000, search: "" },
    { skip: !isSuperAdmin }
  );

  // Fetch dashboard stats
  const { 
    data: dashboardStatsResponse, 
    isLoading: isLoadingStats, 
    error: statsError,
    dataUpdatedAt 
  } = useGetDashboardStatsQuery({ 
    period, 
    companyId: companyId || undefined 
  });

  // Fetch dashboard overview chart data
  const { 
    data: overviewResponse, 
    isLoading: isLoadingOverview, 
    error: overviewError 
  } = useGetDashboardOverviewQuery({ 
    period, 
    companyId: companyId || undefined 
  });

  const dashboardStats = dashboardStatsResponse?.data || {
    companies: { value: 0, change: "0%", changeType: "neutral" },
    activities: { value: 0, change: "0%", changeType: "neutral" },
    guides: { value: 0, change: "0%", changeType: "neutral" },
    formsFilled: { value: 0, change: "0%", changeType: "neutral" },
    lastUpdated: new Date().toISOString(),
  };

  // Track the last updated timestamp - store it when query params change
  const [lastUpdatedTimestamp, setLastUpdatedTimestamp] = useState(null);
  const previousParamsRef = useRef({ period: null, companyId: null });

  // Update timestamp when query parameters change (new fetch) or when data loads
  useEffect(() => {
    const paramsChanged = 
      previousParamsRef.current.period !== period ||
      previousParamsRef.current.companyId !== companyId;

    if (paramsChanged || (!lastUpdatedTimestamp && dashboardStatsResponse?.data)) {
      // Query params changed or first load - update timestamp
      const timestamp = dataUpdatedAt && dataUpdatedAt > 0
        ? new Date(dataUpdatedAt).toISOString()
        : dashboardStats.lastUpdated || new Date().toISOString();
      setLastUpdatedTimestamp(timestamp);
      previousParamsRef.current = { period, companyId };
    }
  }, [period, companyId, dataUpdatedAt, dashboardStatsResponse, dashboardStats.lastUpdated, lastUpdatedTimestamp]);

  const companies = companiesData?.data?.companies || companiesData?.companies || [];
  const companyOptions = companies.map((company) => ({
    code: company._id?.toString() || company.id?.toString(),
    name: company.name || "Unknown Company",
  }));

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  const handleCompanyChange = (newCompanyId) => {
    setCompanyId(newCompanyId);
  };

  // Format last updated time
  const formatLastUpdated = (isoString) => {
    if (!isoString) return "Just now";
    try {
      const date = new Date(isoString);
      const now = new Date();
      
      // Check if date is valid
      if (isNaN(date.getTime())) return "Just now";
      
      // Calculate difference in milliseconds
      const diffMs = now.getTime() - date.getTime();
      
      // If negative (future date), return "Just now"
      if (diffMs < 0) return "Just now";
      
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return "Just now";
      if (diffMins === 1) return "1 min ago";
      if (diffMins < 60) return `${diffMins} mins ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours === 1) return "1 hour ago";
      if (diffHours < 24) return `${diffHours} hours ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays === 1) return "1 day ago";
      return `${diffDays} days ago`;
    } catch (error) {
      return "Just now";
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Stack spacing={2.5} sx={{ alignSelf: "stretch" }}>
        {/* Header Section */}
        <Box
          sx={{
            alignSelf: "stretch",
            minHeight: { xs: "auto", sm: 60 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 2 },
          }}
        >
          <PeriodSelector period={period} onPeriodChange={handlePeriodChange} />
          {isSuperAdmin && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                width: { xs: "100%", sm: 272.84 },
                minHeight: { xs: "auto", sm: 36 },
                alignSelf: { xs: "stretch", sm: "auto" },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  component="img"
                  src={HandshakeIcon}
                  alt="Company"
                  sx={{
                    width: 20,
                    height: 20,
                    objectFit: "contain",
                  }}
                />
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 12, sm: 13 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    lineHeight: 1.85,
                  }}
                >
                  Company
                </Typography>
              </Stack>

              <FormControl
                sx={{
                  flex: "1 1 0",
                  alignSelf: "stretch",
                }}
              >
                <Select
                  value={companyId}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected || selected === "") {
                      return "All Companies";
                    }
                    const selectedCompany = companyOptions.find(
                      (company) => company.code === selected
                    );
                    return selectedCompany?.name || "All Companies";
                  }}
                  sx={{
                    paddingLeft: { xs: 1, sm: 1.5 },
                    paddingRight: { xs: 1, sm: 1.5 },
                    paddingTop: { xs: 0.75, sm: 1 },
                    paddingBottom: { xs: 0.75, sm: 1 },
                    background: "white",
                    borderRadius: 2,
                    outline: `1px ${theme.palette.neutral[100]} solid`,
                    outlineOffset: "-1px",
                    minHeight: { xs: 36, sm: "auto" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiSelect-select": {
                      paddingRight: "32px !important",
                      color: theme.palette.neutral[950],
                      fontSize: { xs: 12, sm: 13 },
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 400,
                      lineHeight: 1.85,
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                  disabled={isLoadingCompanies}
                  IconComponent={(props) => (
                    <Box
                      component="img"
                      src={DownArrowIcon}
                      alt="Dropdown"
                      {...props}
                      sx={{
                        width: 24,
                        height: 24,
                        marginRight: 1,
                        pointerEvents: "auto",
                        cursor: "pointer",
                      }}
                    />
                  )}
                >
                  <MenuItem value="">All Companies</MenuItem>
                  {companyOptions.map((company) => (
                    <MenuItem key={company.code} value={company.code}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}
        </Box>

        {/* Period and Status Card */}
        <CollapsibleSection
          title="Period & Status"
          defaultExpanded={true}
          showCard={true}
        >
          <Box
            sx={{
              alignSelf: "stretch",
              minHeight: { xs: "auto", sm: 36 },
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 1.5, sm: 0 },
            }}
          >
            <PeriodDropdown period={period} onPeriodChange={handlePeriodChange} />
            <StatusIndicator lastUpdated={formatLastUpdated(lastUpdatedTimestamp || dashboardStats.lastUpdated)} />
          </Box>
        </CollapsibleSection>

        {/* KPI Cards */}
        <CollapsibleSection
          title="Key Metrics"
          defaultExpanded={true}
          showCard={false}
        >
          {isLoadingStats ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
              <CircularProgress />
            </Box>
          ) : statsError ? (
            <Alert severity="error">
              {statsError?.data?.message || "Failed to load dashboard statistics"}
            </Alert>
          ) : (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1.5, sm: 2 }}
              sx={{
                alignSelf: "stretch",
                width: "100%",
              }}
            >
              {isSuperAdmin && (
                <KPICard
                  title="Companies"
                  value={dashboardStats.companies.value}
                  change={dashboardStats.companies.change}
                  changeType={dashboardStats.companies.changeType}
                  icon={HandshakeIcon}
                />
              )}
              {isSuperAdmin && (
                <KPICard
                  title="Activities"
                  value={dashboardStats.activities.value}
                  change={dashboardStats.activities.change}
                  changeType={dashboardStats.activities.changeType}
                  icon={ActivityIcon}
                />
              )}
              <KPICard
                title="Users Onboarded"
                value={dashboardStats.guides.value}
                change={dashboardStats.guides.change}
                changeType={dashboardStats.guides.changeType}
                icon={ActivityIcon}
              />
              <KPICard
                title="Forms Submitted"
                value={dashboardStats.formsFilled.value}
                change={dashboardStats.formsFilled.change}
                changeType={dashboardStats.formsFilled.changeType}
                icon={FormIcon}
              />
            </Stack>
          )}
        </CollapsibleSection>

        {/* Chart and Quick Actions */}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={{ xs: 2, sm: 2.5 }}
          sx={{
            alignSelf: "stretch",
          }}
        >
          <Box sx={{ flex: { xs: "1 1 0", lg: "0 0 auto" }, width: { xs: "100%", lg: 752 } }}>
            <CollapsibleSection
              title="Overview"
              defaultExpanded={true}
            >
              <OverviewChart 
                data={overviewResponse?.data || null}
                isLoading={isLoadingOverview}
              />
            </CollapsibleSection>
          </Box>
          <QuickActions />
        </Stack>
      </Stack>
    </Box>
  );
};

export default DashboardPage;
